import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import {
  deleteArchivedPlanFloorSetup,
  readArchivedFolders,
  restorePlanFloorSetup,
} from "../../../utils/planFloorSetupArchiveStorage";
import { logAdminHistoryAction } from "../../../services/AdminHistoryService";
import "./ArchivedPlanFloorSetups.css";

const PAGE_SIZE = 10;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ArchivedPlanFloorSetups = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState("");

  const loadItems = useCallback(() => {
    setItems(readArchivedFolders());
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => {
      const haystack = [
        item.name,
        item.floorName,
        item.salleName,
        item.layoutKey,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const rangeStart =
    filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filtered.length);

  const handleRestore = async (item) => {
    if (!item?.id || busyId) return;
    const name = item.name || t("reservationFolder");

    const result = await Swal.fire({
      title: t("areYouSure") || "Are you sure?",
      text: t("reservationFolderRestoreConfirm", { name }),
      icon: "info",
      showCancelButton: true,
      confirmButtonText: t("reservationFolderRestore"),
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#22c55e",
      cancelButtonColor: "#94a3b8",
      heightAuto: false,
    });
    if (!result.isConfirmed) return;

    setBusyId(item.id);
    try {
      const restored = restorePlanFloorSetup(item.id);
      if (!restored.ok) {
        if (restored.reason === "limit") {
          toast.warning(t("reservationFolderRestoreLimit"));
        } else if (restored.reason === "name_exists") {
          toast.warning(t("reservationFolderNameExists", { name }));
        } else {
          toast.error(t("reservationFolderRestoreError"));
        }
        return;
      }
      toast.success(t("reservationFolderRestored", { name }));
      void logAdminHistoryAction({
        action: "plan_floor_setup.restore",
        description: `Restored plan floor setup "${name}"`,
        metadata: {
          folderId: item.id,
          name,
          floorName: item.floorName || "",
          salleName: item.salleName || "",
          layoutKey: restored.layoutKey || item.layoutKey || "",
        },
      });
      loadItems();
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (item) => {
    if (!item?.id || busyId) return;
    const name = item.name || t("reservationFolder");

    const result = await Swal.fire({
      title: t("areYouSure") || "Are you sure?",
      text: t("reservationFolderDeleteConfirm", { name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("reservationFolderDelete"),
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
      heightAuto: false,
    });
    if (!result.isConfirmed) return;

    setBusyId(item.id);
    try {
      const deleted = deleteArchivedPlanFloorSetup(item.id);
      if (!deleted.ok) {
        toast.error(t("reservationFolderDeleteError"));
        return;
      }
      toast.success(t("reservationFolderDeleted"));
      void logAdminHistoryAction({
        action: "plan_floor_setup.delete",
        description: `Permanently deleted plan floor setup "${name}"`,
        metadata: {
          folderId: item.id,
          name,
          floorName: item.floorName || "",
          salleName: item.salleName || "",
          layoutKey: item.layoutKey || "",
        },
      });
      loadItems();
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <PageTitle
        motherMenu={t("Archived")}
        activeMenu={t("navArchivedPlanFloorSetups")}
      />

      <div className="apfs-page">
        <header className="apfs-hero">
          <div className="apfs-hero__copy">
            <span className="apfs-hero__mark" aria-hidden="true">
              <i className="fas fa-folder-open" />
            </span>
            <div>
              <h2 className="apfs-hero__title">
                {t("archivedPlanFloorSetupsTitle")}
              </h2>
              <p className="apfs-hero__subtitle">
                {t("archivedPlanFloorSetupsSubtitle")}
              </p>
            </div>
          </div>
          <div className="apfs-stat">
            <strong>{filtered.length}</strong>
            <span>{t("reservationFoldersTitle")}</span>
          </div>
        </header>

        <section className="apfs-panel">
          <div className="apfs-toolbar">
            <div className="apfs-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("clientsSearchPlaceholder")}
                aria-label={t("clientsSearchPlaceholder")}
              />
            </div>
            <span className="apfs-count">
              {filtered.length === 0
                ? `0 ${t("reservationFolder").toLowerCase()}`
                : `${rangeStart}–${rangeEnd} / ${filtered.length}`}
            </span>
          </div>

          {pageItems.length === 0 ? (
            <div className="apfs-empty">
              <i className="fas fa-archive" aria-hidden="true" />
              <strong>{t("archivedPlanFloorSetupsEmpty")}</strong>
              <span>{t("archivedPlanFloorSetupsEmptyHint")}</span>
            </div>
          ) : (
            <div className="apfs-table-wrap">
              <table className="apfs-table">
                <thead>
                  <tr>
                    <th scope="col">{t("reservationFolderNameLabel")}</th>
                    <th scope="col">{t("archivedPlanFloorSetupsFloor")}</th>
                    <th scope="col">{t("archivedPlanFloorSetupsRoom")}</th>
                    <th scope="col">{t("archivedPlanFloorSetupsTables")}</th>
                    <th scope="col">{t("archivedPlanFloorSetupsArchivedAt")}</th>
                    <th scope="col" className="is-actions">
                      {t("actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((item) => {
                    const busy = busyId === item.id;
                    const tableCount = Array.isArray(item.tables)
                      ? item.tables.length
                      : 0;
                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="apfs-name">
                            <span className="apfs-name__icon" aria-hidden="true">
                              <i
                                className={`fas ${
                                  tableCount ? "fa-folder-open" : "fa-folder"
                                }`}
                              />
                            </span>
                            <strong>
                              {item.name || t("reservationFolder")}
                            </strong>
                          </div>
                        </td>
                        <td>{item.floorName || "—"}</td>
                        <td>{item.salleName || "—"}</td>
                        <td>
                          <span className="apfs-pill">{tableCount}</span>
                        </td>
                        <td>{formatDate(item.archivedAt)}</td>
                        <td className="is-actions">
                          <div className="apfs-actions">
                            <button
                              type="button"
                              className="apfs-icon-btn is-restore"
                              disabled={busy}
                              onClick={() => handleRestore(item)}
                              title={t("reservationFolderRestore")}
                              aria-label={t("reservationFolderRestore")}
                            >
                              <i className="fas fa-undo" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="apfs-icon-btn is-delete"
                              disabled={busy}
                              onClick={() => handleDelete(item)}
                              title={t("reservationFolderDelete")}
                              aria-label={t("reservationFolderDelete")}
                            >
                              <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filtered.length > PAGE_SIZE ? (
            <div className="apfs-pagination">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                {t("previous")}
              </button>
              <span>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                {t("next")}
              </button>
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
};

export default ArchivedPlanFloorSetups;
